import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { EmploymentAgreementService } from 'src/app/data/employment-agreement.service';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { MultipleWorkerAgreementsActionSelectedWorkersDto } from 'src/app/models/dtos/multiple-worker-agreements-action-selected-workers-dto';
import { BasicInformationStepFormGroupService } from '../../services/basic-information-step-form-group.service';
import { InsuranceStepFormGroupService } from '../../services/insurance-step-form-group.service';
import { WageParametersStepFormGroupService } from '../../services/wage-parameters-step-form-group.service';
import { CheckEmploymentHistoryDialogService } from 'src/app/shared/messages/check-employment-history-dialog/service/check-employment-history-dialog.service';

@Component({
  selector: 'app-multiple-employment-agreement-form',
  templateUrl: './multiple-employment-agreement-form.component.html',
  styleUrls: ['./multiple-employment-agreement-form.component.scss'],
  standalone: false
})
export class MultipleEmploymentAgreementFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private insuranceStepFormGroupService = inject(InsuranceStepFormGroupService);
  private basicInformationStepFormGroupService = inject(BasicInformationStepFormGroupService);
  private wageParametersStepFormGroupService = inject(WageParametersStepFormGroupService);
  private employmentAgreementService = inject(EmploymentAgreementService);
  private spinner = inject(NgxSpinnerService);
  private checkEmploymentHistoryDialogService = inject(CheckEmploymentHistoryDialogService);

  selectedWorkers: MultipleWorkerAgreementsActionSelectedWorkersDto[];
  isExtension: boolean;
  hasEmploymentConditionsConfirmations: boolean;
  prefetchedAgreement: EmploymentAgreementDto;
  agreementId: number;
  firstWorkerId: number;
  hasMinimalWageValidation: boolean;
  employmentTypeId: number;

  insuranceStepFormGroup: UntypedFormGroup;
  basicInformationStepFormGroup: UntypedFormGroup;
  wageFormGroup: UntypedFormGroup;
  wageParametersStepFormGroup: UntypedFormGroup;


  constructor() {
    this.employmentTypeId = this.route.snapshot.params.employmentTypeId;
    this.selectedWorkers = this.getSelectedWorkers();
    this.isExtension = this.getExtension();
    this.agreementId = this.selectedWorkers?.[0]?.AgreementId ?? 0;
    this.firstWorkerId = this.selectedWorkers?.[0]?.Id ?? 0;

    this.insuranceStepFormGroup = this.insuranceStepFormGroupService.buildInsuranceStepFormGroup();
    this.basicInformationStepFormGroup = this.basicInformationStepFormGroupService.buildBasicInformationStepFormGroup();
    this.wageParametersStepFormGroup = this.wageParametersStepFormGroupService.buildWageParametersStepFormGroup(this.employmentTypeId, true);
  }

  get selectedWorkerIds(): number[] {
    return this.selectedWorkers.map((w) => w.Id);
  }
  get agreementTypeId(): number {
    return this.basicInformationStepFormGroup.get('agreementType').value;
  }
  get employmentDateFrom(): number {
    return this.basicInformationStepFormGroup.get('employmentDateFrom').value;
  }
  get employmentDateTo(): number {
    return this.basicInformationStepFormGroup.get('employmentDateTo').value;
  }
  get employerId(): number {
    return this.basicInformationStepFormGroup.get('employer').value;
  }

  ngOnInit(): void {
    if (!this.selectedWorkers?.length) {
      this.router.navigate(['/workerAgreements']);
      return;
    }

    if (this.isExtension) {
      this.fetchAgreementById(this.agreementId);
    }
  }

  ngAfterViewInit(): void {
    this.checkEmploymentHistoryDialogService.checkAndOpenModal(this.employmentTypeId);
  }

  onBasicInformationStepChange(agreement: EmploymentAgreementDto): void {
    this.prefetchedAgreement = agreement;
    this.hasEmploymentConditionsConfirmations = this.prefetchedAgreement.HasEmploymentConditionsConfirmations;
  }

  onSelectedWorkersChange(selectedWorkers: MultipleWorkerAgreementsActionSelectedWorkersDto[]): void {
    this.selectedWorkers = selectedWorkers;
  }

  private getExtension = () => !!history.state.extension;

  private fetchAgreementById(agreementId: number) {
    if (!agreementId) return;

    this.spinner.show();
    this.employmentAgreementService
      .getExtensionMultipleEmploymentAgreementBaseData(agreementId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((agreement) => {
        this.prefetchedAgreement = agreement;
        this.hasEmploymentConditionsConfirmations = this.prefetchedAgreement.HasEmploymentConditionsConfirmations;

        this.selectedWorkers = this.selectedWorkers?.map((worker) => ({
          ...worker,
          CompanyId: worker?.CompanyId ?? this.prefetchedAgreement?.CompanyId,
          CompanyName: worker?.CompanyName,
        }));

        this.basicInformationStepFormGroupService.patchFormGroupValue(this.basicInformationStepFormGroup, this.prefetchedAgreement);
      });
  }

  private getSelectedWorkers = () => {
    const workersFromHistory = history.state.workers;

    if (!!workersFromHistory?.length) {
      sessionStorage.setItem('selectedWorkers', JSON.stringify(workersFromHistory));
      return workersFromHistory;
    }

    const workersFromSessionStorage = sessionStorage.getItem('selectedWorkers');
    if (!!workersFromSessionStorage) {
      return JSON.parse(workersFromSessionStorage);
    }

    return [];
  };
}
